import React, { useEffect, useMemo, useState } from "react";
import { api, apiError, BACKEND_URL } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Layers, Save, X, Image as ImageIcon, Video, FileText } from "lucide-react";

function iconFor(kind) {
  if (kind === "photo") return <ImageIcon className="h-4 w-4" />;
  if (kind === "video") return <Video className="h-4 w-4" />;
  return <FileText className="h-4 w-4" />;
}

function Thumb({ a }) {
  const rel = a?.dropbox_url || a?.url;
  const src = rel ? `${BACKEND_URL}${rel}` : null;
  if (!src)
    return (
      <div className="h-full w-full grid place-items-center bg-[var(--mp-dash-bg)] text-[var(--mp-muted)]">
        {iconFor(a?.kind)}
      </div>
    );
  if (a.kind === "photo")
    return <img src={src} alt="" className="h-full w-full object-cover" />;
  if (a.kind === "video")
    return (
      <video src={src} className="h-full w-full object-cover bg-black" muted preload="metadata" />
    );
  return (
    <div className="h-full w-full grid place-items-center bg-[var(--mp-dash-bg)] text-[var(--mp-muted)]">
      <FileText className="h-6 w-6" />
    </div>
  );
}

export default function ManageGroupDialog({
  open,
  onOpenChange,
  groupId,
  groupName,
  clientId,
  currentItems,
  onSaved,
}) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const currentIds = useMemo(
    () => new Set((currentItems || []).map((m) => m.id)),
    [currentItems]
  );

  useEffect(() => {
    if (!open || !clientId) return;
    (async () => {
      setLoading(true);
      try {
        // Fetch all tasks across the 3 statuses for this client
        const results = await Promise.all(
          ["todo", "edited", "published"].map((s) =>
            api
              .get(`/staff/tasks`, { params: { status: s } })
              .then((r) => r.data?.items || [])
              .catch(() => [])
          )
        );
        const all = [].concat(...results);
        const mine = all.filter((m) => m.client?.id === clientId);
        // Dedup by id
        const seen = new Set();
        const dedup = [];
        for (const m of mine) {
          if (!seen.has(m.id)) {
            seen.add(m.id);
            dedup.push(m);
          }
        }
        setCandidates(dedup);
        // Pre-select items currently in the group
        setSelectedIds(new Set(dedup.filter((m) => m.group_id === groupId).map((m) => m.id)));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, clientId, groupId]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const save = async () => {
    const toAdd = [];
    const toRemove = [];
    for (const m of candidates) {
      const wasIn = currentIds.has(m.id) || m.group_id === groupId;
      const isIn = selectedIds.has(m.id);
      if (!wasIn && isIn) toAdd.push(m.id);
      else if (wasIn && !isIn) toRemove.push(m.id);
    }
    if (toAdd.length === 0 && toRemove.length === 0) {
      toast.info("Nessuna modifica");
      onOpenChange(false);
      return;
    }
    // Block if leaving only 1 item in the group (groups need 2+)
    const finalCount = candidates.filter((m) => selectedIds.has(m.id)).length;
    if (finalCount < 2) {
      toast.error("Un gruppo deve contenere almeno 2 contenuti. Usa 'Rimuovi gruppo' per scioglierlo.");
      return;
    }
    setSaving(true);
    try {
      if (toRemove.length > 0) {
        await api.post(`/staff/groups/${groupId}/remove`, { message_ids: toRemove });
      }
      if (toAdd.length > 0) {
        await api.post(`/staff/groups/${groupId}/add`, { message_ids: toAdd });
      }
      toast.success(
        `Gruppo aggiornato: +${toAdd.length} / −${toRemove.length}`
      );
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="manage-group-dialog"
        className="max-w-2xl rounded-3xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Layers className="h-5 w-5" /> Modifica gruppo
          </DialogTitle>
          <DialogDescription>
            {groupName ? `"${groupName}" · ` : ""}seleziona i contenuti che devono far parte del gruppo.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="p-10 text-center text-[var(--mp-muted)]">Caricamento…</div>
        ) : candidates.length === 0 ? (
          <div className="p-10 text-center text-[var(--mp-muted)]">
            Nessun altro contenuto disponibile per questo cliente.
          </div>
        ) : (
          <div className="max-h-[55vh] overflow-y-auto pr-1 space-y-1.5">
            {candidates.map((m) => {
              const a = m.attachment || {};
              const fileName = a.display_name || a.file_name || a.kind || "file";
              const isIn = selectedIds.has(m.id);
              const inOther = m.group_id && m.group_id !== groupId;
              return (
                <label
                  key={m.id}
                  data-testid={`manage-group-item-${m.id}`}
                  className={`flex items-center gap-3 p-2 rounded-xl border cursor-pointer transition-colors ${
                    isIn
                      ? "border-[var(--mp-ink)] bg-[var(--mp-yellow)]/30"
                      : "border-[var(--mp-border)] bg-white hover:border-[var(--mp-ink)]"
                  } ${inOther && !isIn ? "opacity-60" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={isIn}
                    onChange={() => toggle(m.id)}
                    className="h-4 w-4 accent-[var(--mp-ink)]"
                    data-testid={`manage-group-check-${m.id}`}
                  />
                  <div className="h-14 w-14 shrink-0 rounded-lg overflow-hidden border border-[var(--mp-border)]">
                    <Thumb a={a} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{fileName}</div>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[var(--mp-muted)]">
                      {new Date(m.created_at).toLocaleDateString("it-IT", {
                        day: "2-digit",
                        month: "2-digit",
                      })}
                      {inOther && (
                        <span className="ml-2 text-red-600 normal-case tracking-normal">
                          · in un altro gruppo ({m.group_name || "senza nome"})
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-full border-[var(--mp-ink)]"
            data-testid="manage-group-cancel"
          >
            <X className="h-4 w-4 mr-1" /> Annulla
          </Button>
          <Button
            onClick={save}
            disabled={saving || loading}
            data-testid="manage-group-save"
            className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
          >
            <Save className="h-4 w-4 mr-1" />
            {saving ? "Salvo…" : "Salva modifiche"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
